import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useRef, useState } from "react";
import { BarChart3, BookOpen, LifeBuoy, LogOut, Monitor, Moon, Palette, SettingsIcon, Sun, Wrench } from "lucide-react";
import { useTheme } from "next-themes";
import posthog from "posthog-js";
import { AuthContext } from "../AuthProvider";
import { githubLogin } from "@/lib/github-login";
import { NotificationFloatingButton } from "../NotificationFloatingButton";
import { StatusWidgetMorph, RailStatusHoverCard } from "./StatusIndicator";
import { BSErrorBoundary } from "../ui/bs-error-boundary";
import { DropdownMenu, DropdownMenuContent, DropdownMenuControlOption, DropdownMenuControlRow, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { Button } from "@repo/blacksmith-ui/button";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { useRoutePrefetchIntent } from "@/hooks/useRoutePrefetchIntent";
import { useIsHydrated } from "@/components/codesmith/useIsHydrated";
import { cn } from "@repo/blacksmith-ui/cn";
import { RAIL_REDESIGN_WIDTH, RAIL_WIDTH } from "./railConstants";
export type SidebarActiveSection = "home" | "actions" | "codesmith" | "proof" | "usage" | "admin";
export const RAIL_ROW = cn("rail-row flex h-8 w-full items-center overflow-hidden rounded-md cursor-pointer transition-none", "type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950", "[&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900", "dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]");
export const RAIL_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
export const RAIL_REDESIGN_CLASS = "[&_.rail-row]:rounded-[4px] [&_.rail-row-icon]:w-full [&_.rail-row-icon>svg]:size-4";
export function RailRowContents(t0) {
  const $ = _c(15);
  const {
    icon,
    label,
    badge,
    expanded: t1,
    hideLabelWhenCollapsed: t2
  } = t0;
  const expanded = t1 === undefined ? true : t1;
  const hideLabelWhenCollapsed = t2 === undefined ? true : t2;
  const collapsedAndRemoved = hideLabelWhenCollapsed && !expanded;
  let t3;
  if ($[0] !== icon) {
    t3 = <span className="rail-row-icon flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = collapsedAndRemoved && "hidden";
  const t5 = !badge && "pr-2";
  let t6;
  if ($[2] !== t4 || $[3] !== t5) {
    t6 = cn("whitespace-nowrap text-left", t4, t5);
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== label || $[6] !== t6) {
    t7 = <span className={t6}>{label}</span>;
    $[5] = label;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== badge || $[9] !== collapsedAndRemoved) {
    t8 = badge && !collapsedAndRemoved ? <span className="ml-auto mr-2 shrink-0">{badge}</span> : null;
    $[8] = badge;
    $[9] = collapsedAndRemoved;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t3 || $[12] !== t7 || $[13] !== t8) {
    t9 = <>{t3}{t7}{t8}</>;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
const THEME_OPTIONS = [{
  value: "system",
  label: "System",
  icon: Monitor
}, {
  value: "light",
  label: "Light",
  icon: Sun
}, {
  value: "dark",
  label: "Dark",
  icon: Moon
}] as const;
export function AppearanceThemeToggle() {
  const $ = _c(4);
  const {
    theme
  } = useTheme();
  const setTheme = useSetPersistedTheme();
  const mounted = useIsHydrated();
  const activeTheme = mounted ? theme ?? "system" : "system";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = THEME_OPTIONS.map(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== activeTheme || $[2] !== setTheme) {
    t1 = <DropdownMenuRadioGroup value={activeTheme} onValueChange={setTheme} className="-mr-1 flex items-center gap-0.5">{t0}</DropdownMenuRadioGroup>;
    $[1] = activeTheme;
    $[2] = setTheme;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// A right-anchored label tooltip for a collapsed rail/sidebar row. Used both by
// the icon-collapsible sidebar and by the slim app-switcher rail (which no
// longer hover-expands to reveal labels, so each row surfaces its label here).
function _temp(t0) {
  const {
    value,
    label,
    icon: Icon
  } = t0;
  return <DropdownMenuControlOption key={value} value={value} aria-label={label} textValue={label}><Icon /></DropdownMenuControlOption>;
}
export function CollapsedIconTooltip(t0) {
  const $ = _c(8);
  const {
    label,
    enabled,
    shortcut,
    children
  } = t0;
  if (!enabled) {
    return children;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipTrigger asChild={true}>{children}</TooltipTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== shortcut) {
    t2 = shortcut ? <TooltipContent side="right" className="flex items-center gap-2"><span className="leading-none">{label}</span><ShortcutKeys keys={[shortcut]} /></TooltipContent> : <TooltipContent side="right">{label}</TooltipContent>;
    $[2] = label;
    $[3] = shortcut;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function useSidebarLogout() {
  const $ = _c(2);
  const {
    logout
  } = useContext(AuthContext);
  let t0;
  if ($[0] !== logout) {
    t0 = () => {
      posthog?.reset();
      logout();
    };
    $[0] = logout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function SidebarAdminRow(t0) {
  const $ = _c(19);
  const {
    expanded,
    active,
    onClick,
    hideLabelWhenCollapsed: t1,
    divider: t2
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const divider = t2 === undefined ? true : t2;
  const {
    user
  } = useContext(AuthContext);
  if (!user?.is_superuser) {
    return null;
  }
  let t3;
  if ($[0] !== divider) {
    t3 = divider && <div className="my-2"><hr className="border-[#0c0c0d14] dark:border-white/12" /></div>;
    $[0] = divider;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = hideLabelWhenCollapsed && !expanded;
  const t5 = active ? "page" : undefined;
  const t6 = active && RAIL_ROW_ACTIVE;
  let t7;
  if ($[2] !== t6) {
    t7 = cn(RAIL_ROW, t6, "group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:justify-center");
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Wrench className="size-[18px]" />;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== expanded || $[6] !== hideLabelWhenCollapsed) {
    t9 = <RailRowContents icon={t8} label="Admin" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[5] = expanded;
    $[6] = hideLabelWhenCollapsed;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== onClick || $[9] !== t5 || $[10] !== t7 || $[11] !== t9) {
    t10 = <RoutePrefetchLink href="/admin" onClick={onClick} aria-label="Admin" aria-current={t5} className={t7}>{t9}</RoutePrefetchLink>;
    $[8] = onClick;
    $[9] = t5;
    $[10] = t7;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t4) {
    t11 = <CollapsedIconTooltip label="Admin" enabled={t4}>{t10}</CollapsedIconTooltip>;
    $[13] = t10;
    $[14] = t4;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t3) {
    t12 = <>{t3}{t11}</>;
    $[16] = t11;
    $[17] = t3;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}
export function SidebarGlobalFooter(t0) {
  const $ = _c(91);
  const {
    expanded,
    currentOrg,
    settingsHref,
    activeSection,
    onSectionClick,
    className,
    hideLabelWhenCollapsed: t1,
    statusAsHoverCard: t2,
    redesign: t3,
    leading
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const statusAsHoverCard = t2 === undefined ? false : t2;
  const redesign = t3 === undefined ? false : t3;
  const {
    user
  } = useContext(AuthContext);
  let t4;
  if ($[0] !== user) {
    t4 = githubLogin(user);
    $[0] = user;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const login = t4;
  const handleLogout = useSidebarLogout();
  const usageHref = currentOrg ? `/${currentOrg}/usage` : "/usage";
  const supportHref = currentOrg ? `/${currentOrg}/support` : "/support";
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsTriggerRef = useRef(null);
  const settingsPrefetchIntent = useRoutePrefetchIntent(currentOrg ? settingsHref : null);
  const settingsMenuCloseTimer = useRef(null);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (settingsMenuCloseTimer.current) {
        clearTimeout(settingsMenuCloseTimer.current);
        settingsMenuCloseTimer.current = null;
      }
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const cancelSettingsMenuClose = t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => {
      if (event.pointerType === "touch") {
        return;
      }
      cancelSettingsMenuClose();
      setSettingsMenuOpen(true);
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const openSettingsMenu = t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event_0 => {
      if (event_0.pointerType === "touch") {
        return;
      }
      cancelSettingsMenuClose();
      settingsMenuCloseTimer.current = setTimeout(() => setSettingsMenuOpen(false), 120);
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const scheduleSettingsMenuClose = t7;
  let t8;
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => cancelSettingsMenuClose;
    t9 = [];
    $[5] = t8;
    $[6] = t9;
  } else {
    t8 = $[5];
    t9 = $[6];
  }
  useEffect(t8, t9);
  let t10;
  if ($[7] !== expanded || $[8] !== hideLabelWhenCollapsed) {
    t10 = <NotificationFloatingButton variant="sidebar" label="Notifications" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[7] = expanded;
    $[8] = hideLabelWhenCollapsed;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const notifications = t10;
  const t11 = redesign ? undefined : "pt-1";
  let t12;
  if ($[10] !== expanded || $[11] !== redesign || $[12] !== statusAsHoverCard) {
    t12 = statusAsHoverCard ? <RailStatusHoverCard railWidth={redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH} /> : <StatusWidgetMorph expanded={expanded} />;
    $[10] = expanded;
    $[11] = redesign;
    $[12] = statusAsHoverCard;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== t11 || $[15] !== t12) {
    t13 = <BSErrorBoundary title="Status Indicator" fallback={null}><div className={t11}>{t12}</div></BSErrorBoundary>;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const status = t13;
  let t14;
  if ($[17] !== settingsMenuOpen) {
    t14 = event_1 => {
      if (settingsMenuOpen && event_1.pointerType !== "touch" && event_1.button === 0 && !event_1.ctrlKey) {
        event_1.preventDefault();
      }
    };
    $[17] = settingsMenuOpen;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SettingsIcon className="size-[18px]" />;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== expanded || $[21] !== hideLabelWhenCollapsed) {
    t16 = <RailRowContents icon={t15} label="Settings" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[20] = expanded;
    $[21] = hideLabelWhenCollapsed;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== settingsPrefetchIntent.onFocus || $[24] !== settingsPrefetchIntent.onMouseEnter || $[25] !== t14 || $[26] !== t16) {
    t17 = <DropdownMenuTrigger asChild={true}><button ref={settingsTriggerRef} type="button" aria-label="Settings menu" className={RAIL_ROW} onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onMouseEnter={settingsPrefetchIntent.onMouseEnter} onFocus={settingsPrefetchIntent.onFocus} onPointerDown={t14}>{t16}</button></DropdownMenuTrigger>;
    $[23] = settingsPrefetchIntent.onFocus;
    $[24] = settingsPrefetchIntent.onMouseEnter;
    $[25] = t14;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = event_3 => {
      const target = event_3.target;
      if (target instanceof Node && settingsTriggerRef.current?.contains(target)) {
        event_3.preventDefault();
      }
    };
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <SettingsIcon />;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const t20 = !!currentOrg;
  let t21;
  if ($[30] !== settingsHref || $[31] !== t20) {
    t21 = <DropdownMenuItem asChild={true} icon={t19}><RoutePrefetchLink href={settingsHref} prefetchOnIntent={t20}>Settings</RoutePrefetchLink></DropdownMenuItem>;
    $[30] = settingsHref;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <LifeBuoy />;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  const t23 = !!currentOrg;
  let t24;
  if ($[34] !== supportHref || $[35] !== t23) {
    t24 = <DropdownMenuItem asChild={true} icon={t22}><RoutePrefetchLink href={supportHref} prefetchOnIntent={t23}>Contact Support</RoutePrefetchLink></DropdownMenuItem>;
    $[34] = supportHref;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <DropdownMenuItem asChild={true} icon={<BookOpen />}><a href="https://docs.blacksmith.sh" target="_blank" rel="noopener noreferrer">Documentation...</a></DropdownMenuItem>;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Palette />;
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  let t28;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <DropdownMenuControlRow icon={t26} label="Appearance"><AppearanceThemeToggle /></DropdownMenuControlRow>;
    t28 = <DropdownMenuSeparator />;
    $[39] = t27;
    $[40] = t28;
  } else {
    t27 = $[39];
    t28 = $[40];
  }
  let t29;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <LogOut />;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== login) {
    t30 = login ? <span className="block max-w-[200px] truncate" title={login}>{login}</span> : undefined;
    $[42] = login;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== handleLogout || $[45] !== t30) {
    t31 = <DropdownMenuItem icon={t29} trailing={t30} onClick={handleLogout}>Log out</DropdownMenuItem>;
    $[44] = handleLogout;
    $[45] = t30;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] !== t21 || $[48] !== t24 || $[49] !== t31) {
    t32 = <DropdownMenuContent side="right" align="start" sideOffset={8} alignOffset={-31} className="min-w-[216px] max-w-[320px]" onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onCloseAutoFocus={_temp2} onPointerDownOutside={t18}>{t21}{t24}{t25}{t27}{t28}{t31}</DropdownMenuContent>;
    $[47] = t21;
    $[48] = t24;
    $[49] = t31;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] !== settingsMenuOpen || $[52] !== t17 || $[53] !== t32) {
    t33 = <DropdownMenu modal={false} open={settingsMenuOpen} onOpenChange={setSettingsMenuOpen}>{t17}{t32}</DropdownMenu>;
    $[51] = settingsMenuOpen;
    $[52] = t17;
    $[53] = t32;
    $[54] = t33;
  } else {
    t33 = $[54];
  }
  const settingsMenu = t33;
  const t34 = hideLabelWhenCollapsed && !expanded;
  const t35 = currentOrg ? true : null;
  const t36 = !!currentOrg;
  let t37;
  if ($[55] !== onSectionClick) {
    t37 = onSectionClick?.("usage");
    $[55] = onSectionClick;
    $[56] = t37;
  } else {
    t37 = $[56];
  }
  const t38 = activeSection === "usage" ? "page" : undefined;
  const t39 = activeSection === "usage" && RAIL_ROW_ACTIVE;
  let t40;
  if ($[57] !== t39) {
    t40 = cn(RAIL_ROW, t39);
    $[57] = t39;
    $[58] = t40;
  } else {
    t40 = $[58];
  }
  let t41;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <BarChart3 className="size-[18px]" />;
    $[59] = t41;
  } else {
    t41 = $[59];
  }
  let t42;
  if ($[60] !== expanded || $[61] !== hideLabelWhenCollapsed) {
    t42 = <RailRowContents icon={t41} label="Usage and Billing" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[60] = expanded;
    $[61] = hideLabelWhenCollapsed;
    $[62] = t42;
  } else {
    t42 = $[62];
  }
  let t43;
  if ($[63] !== t35 || $[64] !== t36 || $[65] !== t37 || $[66] !== t38 || $[67] !== t40 || $[68] !== t42 || $[69] !== usageHref) {
    t43 = <RoutePrefetchLink href={usageHref} prefetch={t35} prefetchOnIntent={t36} onClick={t37} aria-label="Usage and Billing" aria-current={t38} className={t40}>{t42}</RoutePrefetchLink>;
    $[63] = t35;
    $[64] = t36;
    $[65] = t37;
    $[66] = t38;
    $[67] = t40;
    $[68] = t42;
    $[69] = usageHref;
    $[70] = t43;
  } else {
    t43 = $[70];
  }
  let t44;
  if ($[71] !== t34 || $[72] !== t43) {
    t44 = <CollapsedIconTooltip label="Usage and Billing" enabled={t34}>{t43}</CollapsedIconTooltip>;
    $[71] = t34;
    $[72] = t43;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  const usage = t44;
  if (redesign) {
    let t45;
    if ($[74] !== className) {
      t45 = cn("rail-rows flex flex-col", className);
      $[74] = className;
      $[75] = t45;
    } else {
      t45 = $[75];
    }
    let t46;
    if ($[76] !== leading || $[77] !== notifications || $[78] !== settingsMenu || $[79] !== status || $[80] !== t45 || $[81] !== usage) {
      t46 = <div className={t45}>{leading}{usage}{notifications}{settingsMenu}{status}</div>;
      $[76] = leading;
      $[77] = notifications;
      $[78] = settingsMenu;
      $[79] = status;
      $[80] = t45;
      $[81] = usage;
      $[82] = t46;
    } else {
      t46 = $[82];
    }
    return t46;
  }
  let t45;
  if ($[83] !== className) {
    t45 = cn("rail-rows flex flex-col gap-1", className);
    $[83] = className;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== notifications || $[86] !== settingsMenu || $[87] !== status || $[88] !== t45 || $[89] !== usage) {
    t46 = <div className={t45}>{usage}{settingsMenu}{notifications}{status}</div>;
    $[85] = notifications;
    $[86] = settingsMenu;
    $[87] = status;
    $[88] = t45;
    $[89] = usage;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  return t46;
}
function _temp2(event_2) {
  return event_2.preventDefault();
}